<script setup>
import { useAbility } from '@casl/vue'
import { useOrderTypeConfigStore } from '@/stores/orderTypeConfigs'

definePage({
  meta: {
    layout: 'default',
    action: 'update',
    subject: 'Settings',
  },
})

const ability = useAbility()
const store = useOrderTypeConfigStore()
const { loading, request } = useApiRequest()
const { formatRupiah } = useFormat()

// 3 tipe order dikunci sesuai ENUM backend (dine_in, takeaway, delivery) —
// bukan data dinamis, jadi ditulis eksplisit di sini.
const ORDER_TYPES = [
  { value: 'dine_in', label: 'Dine-in (Makan di Tempat)', icon: 'ri-restaurant-line' },
  { value: 'takeaway', label: 'Takeaway (Bawa Pulang)', icon: 'ri-shopping-bag-line' },
  { value: 'delivery', label: 'Delivery (Ojek Online)', icon: 'ri-motorbike-line' },
]

const chargeTypeOptions = [
  { title: 'Persentase (%)', value: 'percentage' },
  { title: 'Nominal Tetap (Rp)', value: 'fixed' },
]

// Addendum 16, Modul BV: default toggle antrian kalau tenant belum pernah
// simpan halaman ini sama sekali — SAMA PERSIS dengan default backend
// (QueueService::isQueueEnabledForOrderType() saat row belum ada): Dine-in
// & Takeaway aktif, Delivery tidak (delivery tidak dipanggil di tempat).
const defaultQueueEnabled = orderType => orderType !== 'delivery'

const rows = ref(
  ORDER_TYPES.map(t => ({
    order_type: t.value,
    charge_type: 'percentage',
    charge_value: 0,
    is_active: false,
    queue_enabled: defaultQueueEnabled(t.value),
  }))
)

const loadConfigs = async () => {
  await store.fetchConfigs()

  rows.value = ORDER_TYPES.map(t => {
    const existing = store.getConfig(t.value)
    return existing
      ? {
          order_type: existing.order_type,
          charge_type: existing.charge_type,
          charge_value: Number(existing.charge_value),
          is_active: existing.is_active,
          queue_enabled: existing.queue_enabled,
        }
      : { order_type: t.value, charge_type: 'percentage', charge_value: 0, is_active: false, queue_enabled: defaultQueueEnabled(t.value) }
  })
}

const previewSurcharge = row => {
  const sampleSubtotal = 100000
  if (!row.is_active) return 'Tidak aktif'
  if (row.charge_type === 'percentage') {
    return `${row.charge_value}% (≈ ${formatRupiah(sampleSubtotal * (row.charge_value / 100))} dari subtotal Rp100rb)`
  }
  return formatRupiah(row.charge_value) + ' per order'
}

const save = async () => {
  await request(async () => {
    await store.saveConfigs(rows.value)
  })
}

onMounted(loadConfigs)
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Konfigurasi Tipe Order</h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Atur service charge / markup otomatis untuk Dine-in, Takeaway, dan Delivery
        </p>
      </VCol>
    </VRow>

    <VCard>
      <VCardText>
        <div v-if="store.loading" class="d-flex justify-center pa-8">
          <VProgressCircular indeterminate color="primary" />
        </div>

        <div v-else>
          <div
            v-for="(row, index) in rows"
            :key="row.order_type"
            class="config-row pa-4 mb-3"
          >
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center gap-2">
                <VIcon :icon="ORDER_TYPES[index].icon" color="primary" />
                <span class="text-subtitle-1 font-weight-medium">
                  {{ ORDER_TYPES[index].label }}
                </span>
              </div>
              <VSwitch v-model="row.is_active" color="success" hide-details />
            </div>

            <VRow>
              <VCol cols="12" sm="5">
                <VSelect
                  v-model="row.charge_type"
                  :items="chargeTypeOptions"
                  label="Jenis Biaya"
                  density="compact"
                  :disabled="!row.is_active"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VTextField
                  v-model.number="row.charge_value"
                  type="number"
                  min="0"
                  :label="row.charge_type === 'percentage' ? 'Nilai (%)' : 'Nilai (Rp)'"
                  density="compact"
                  :disabled="!row.is_active"
                />
              </VCol>
              <VCol cols="12" sm="3" class="d-flex align-center">
                <span class="text-caption text-medium-emphasis">
                  {{ previewSurcharge(row) }}
                </span>
              </VCol>
            </VRow>

            <VDivider class="my-3" />

            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="text-body-2 font-weight-medium">Ikut Sistem Antrian</span>
                <div class="text-caption text-medium-emphasis">
                  Order tipe ini dapat nomor antrian otomatis setelah checkout.
                </div>
              </div>
              <VSwitch v-model="row.queue_enabled" color="primary" hide-details />
            </div>
          </div>
        </div>
      </VCardText>

      <VDivider />

      <VCardActions>
        <VSpacer />
        <VBtn
          v-if="ability.can('update', 'Settings')"
          color="primary"
          :loading="loading"
          @click="save"
        >
          Simpan Konfigurasi
        </VBtn>
      </VCardActions>
    </VCard>
  </div>
</template>

<style scoped>
.config-row {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 8px;
}
</style>
